import { FormArray, FormControl, FormGroup } from "@angular/forms";

export type InvoiceProcessorResult = {
  entity: FormGroup<EntityFormGroup> | null,
  equipments: EnergyMeter[],
  invoice: FormGroup<InvoiceDataFormGroup>,
};

export type EnergyMeter = FormGroup<ElectricityEquipmentFormGroup> | FormGroup<NaturalGasEquipmentFormGroup>;

/** --------- ELECTRICITY METER ------------- */
export type ElectricityEquipmentFormGroup = {
  equipmentCode: FormGroup<DataComparisonFormGroup<string | null>>;
  contractedPower: FormGroup<DataComparisonFormGroup<number | null>>;
  tensionLevel: FormGroup<DataComparisonFormGroup<string | null>>;
  timeCycle: FormGroup<DataComparisonFormGroup<string | null>>;
  countType: FormGroup<DataComparisonFormGroup<string | null>>;
  offPeakEnergyPrice: FormControl<number | null>;
  peakEnergyPrice: FormControl<number | null>;
  standardEnergyPrice: FormControl<number | null>;
  superOffPeakEnergyPrice: FormControl<number | null>;

  // Address
  county: FormGroup<DataComparisonFormGroup<string | null>>;
  district: FormGroup<DataComparisonFormGroup<string | null>>;
  locality: FormGroup<DataComparisonFormGroup<string | null>>;
  parish: FormGroup<DataComparisonFormGroup<number | string | null>>;
  street: FormGroup<DataComparisonFormGroup<string | null>>;
  floor: FormGroup<DataComparisonFormGroup<string | null>>;
  door: FormGroup<DataComparisonFormGroup<string | null>>;
  postalCode: FormGroup<DataComparisonFormGroup<string | null>>;

  // Structural
  equipmentId: FormControl<number | null>;
  postalCodeId: FormControl<number | null>;
  consumptions: FormArray<FormGroup<ConsumptionsFormGroup>>;

  // Invoice-specific values
  invoiceData?: FormGroup<InvoiceDataFormGroup>;

  // UI + metadata
  associatedWithOpportunity: FormControl<boolean | null>;
};

/** --------- NATURAL GAS METER ------------- */
export type NaturalGasEquipmentFormGroup = {
  // Comparison fields
  equipmentCode: FormGroup<DataComparisonFormGroup<string | null>>;
  pressureLevel: FormGroup<DataComparisonFormGroup<string | null>>;
  tariffOption: FormGroup<DataComparisonFormGroup<string | null>>;
  tier: FormGroup<DataComparisonFormGroup<string | null>>;
  county: FormGroup<DataComparisonFormGroup<string | null>>;
  district: FormGroup<DataComparisonFormGroup<string | null>>;
  locality: FormGroup<DataComparisonFormGroup<string | null>>;
  parish: FormGroup<DataComparisonFormGroup<number | string | null>>;
  street: FormGroup<DataComparisonFormGroup<string | null>>;
  floor: FormGroup<DataComparisonFormGroup<string | null>>;
  door: FormGroup<DataComparisonFormGroup<string | null>>;
  postalCode: FormGroup<DataComparisonFormGroup<string | null>>;
  energyPrice: FormGroup<DataComparisonFormGroup<number | null>>;

  // Structural / non-comparison
  equipmentId: FormControl<number | null>;
  postalCodeId: FormControl<number | null>;
  consumptions: FormArray<FormGroup<ConsumptionsFormGroup>>;

  invoiceData?: FormGroup<InvoiceDataFormGroup>;

  associatedWithOpportunity: FormControl<boolean | null>;
};

/** --------- METER CONSUMPTIONS ------------ */
export type ConsumptionsFormGroup = {
  date: FormControl<Date | undefined>,
  offPeak: FormControl<number | undefined>,
  peak: FormControl<number | undefined>,
  standard: FormControl<number | undefined>,
  superOffPeak: FormControl<number | undefined>,
  gas: FormControl<number | undefined>,
  id: FormControl<number | undefined>,
  singleLine: FormControl<boolean>;
};

/** --------- ENTITY OF METER --------------- */
export type EntityFormGroup = {
  id: FormControl<string | null>;
  name: FormControl<string | null>;
  tin: FormControl<number | null>;
  email: FormGroup<DataComparisonFormGroup<string | null>>;
  phoneNumber: FormGroup<DataComparisonFormGroup<string | null>>;
  address: FormGroup<DataComparisonFormGroup<string | null>>;
  postalCode: FormGroup<DataComparisonFormGroup<string | number | null>>;
  parish: FormGroup<DataComparisonFormGroup<string | number | null>>;
};

/** --------- INVOICE DATA --------------- */
export type InvoiceDataFormGroup = {

  /**-------- CUSTOMER --------------- */
  customerTin: FormControl<string | null>,
  customerEmail: FormControl<string | null>,
  customerName: FormControl<string | null>,
  customerPhoneNumber: FormControl<string | null>,
  customerParish: FormControl<string | null>,
  customerPostalCode: FormControl<string | null>,
  customerAddress: FormControl<string | null>,

  /**-------- SUPPLIER --------------- */
  supplierName: FormControl<string | null>;
  supplierBrandLogo: FormControl<string | null>;
  supplierId: FormControl<number | null>;

  /**-------- EQUIPMENT ADDRESS ------ */
  supplyAddress: FormControl<string | null>;
  supplyParish: FormControl<string | null>;
  supplyZipCode: FormControl<string | null>;

  /**-------- INVOICE DATA ---------- */
  invoiceDueDate: FormControl<Date | null>;
  invoiceServiceStartDate: FormControl<Date | null>;
  invoiceServiceEndDate: FormControl<Date | null>;
  invoiceTotal: FormControl<number | null>;
  invoicePaymentTerm: FormControl<string | null>;
  invoiceIBAN: FormControl<string | null>;
  isElectricityInvoice: FormControl<boolean | null>;
  isNaturalGasInvoice: FormControl<boolean | null>;
  isPrimaryInvoice: FormControl<boolean | null>;
  file: FormControl<File | null>;

  /**------- ELECTRICITY EQUIPMENT ---------- */
  electricityEquipmentCode: FormControl<string | null>;
  electricityContractedPower: FormControl<number | null>;
  electricityContractedPowerPrice: FormControl<number | null>;
  electricityContractedPowerPHPPrice: FormControl<number | null>;

  electricityCountType: FormControl<string | null>;
  electricityCountTypeId: FormControl<string | number | null>;

  electricityTensionLevel: FormControl<string | null>;
  electricityTensionLevelId: FormControl<string | number | null>;

  electricityTimeCycle: FormControl<string | null>;
  electricityTimeCycleId: FormControl<string | number | null>;

  electricityConsumptionP1: FormControl<number | null>;
  electricityConsumptionP2: FormControl<number | null>;
  electricityConsumptionP3: FormControl<number | null>;
  electricityConsumptionP4: FormControl<number | null>;
  electricityTotalConsumption: FormControl<number | null>;

  electricityPriceP1: FormControl<number | null>;
  electricityPriceP2: FormControl<number | null>;
  electricityPriceP3: FormControl<number | null>;
  electricityPriceP4: FormControl<number | null>;

  /**--------- NATURAL GAS EQUIPMENT ---------- */
  naturalGasEquipmentCode: FormControl<string | null>;

  naturalGasPressureLevel: FormControl<string | null>;
  naturalGasPressureLevelId: FormControl<string | number | null>;

  naturalGasRating: FormControl<string | null>;
  naturalGasRatingId: FormControl<string | number | null>;

  naturalGasTier: FormControl<string | null>;
  naturalGasTierId: FormControl<string | number | null>;

  naturalGasPrice: FormControl<number | null>;
  naturalGasTotalConsumption: FormControl<number | null>;

  raw?: FormControl<any>;
};

export type EnergyType = 'electricity' | 'gas';

/** ------- COMPARISSON FORM GROUP ---------- */
export interface DataComparisonFormGroup<T = any, K = any> {
  invoiceValue: FormControl<NonNullable<T> | null>;
  serverValue: FormControl<NonNullable<T> | null>;
  finalValue: FormControl<NonNullable<T> | null>;
  invoiceId?: FormControl<K | null>;
  serverId?: FormControl<K | null>;
  finalId?: FormControl<K | null>;
  comparisonResult: FormControl<'match' | 'mismatch' | 'notFound' | null>;
}

/** ------- ENUMS WITH ELEC/GAS DATA -------- */
export interface EnumItem { id: string; description: string; }

export interface EnumsDictionary {
  tensionLevels: Map<string, string>;
  timeCycles: Map<string, string>;
  countTypes: Map<string, string>;
  pressureLevels: Map<string, string>;
  tariffOptions: Map<string, string>;
  tiers: Map<string, string>;
}

export const countTypeKeywords = [
  { value: 'simple', keywords: ['simples', 'simple'] },
  { value: 'bihourly', keywords: ['bi-horario', 'bihorario', 'bi-horário', 'bihorária', 'bi horario', 'bi horario'] },
  { value: 'trihourly', keywords: ['tri-horario', 'trihorario', 'tri-horário', 'tri horario', 'tri horario'] },
  { value: 'quadhourly', keywords: ['tetra-horario', 'tetrahora', 'tetrahora', 'tetra horario', 'cuatro horarios', 'quadhorario', 'quad horario', 'cuádruple horario'] },
];

export const timeCycleKeywords = [
  { value: 'withouttimecycle', keywords: ['sem ciclo', 'sem tempo', 'sin ciclo', 'sin tiempo'] },
  { value: 'daily', keywords: ['diario', 'diária', 'diária', 'diaria', 'diari', 'diary'] },
  { value: 'weekly', keywords: ['semanal', 'semana', 'weekly'] },
  { value: 'optionalweekly', keywords: ['opcional', 'opc.', 'opcional semanal', 'opcional semanal'] },
];

export const tariffOptionKeywords = [
  { value: 'monthly', keywords: ['mensal', 'mensual', 'mensalidade', 'mensualmente'] },
  { value: 'shortuses', keywords: ['uso curto', 'uso esporádico', 'uso puntual', 'corto'] },
  { value: 'longuses', keywords: ['uso longo', 'uso prolongado', 'uso largo', 'prolongado'] },
  { value: 'flexiblemonthly', keywords: ['mensal flexível', 'mensal flexivel', 'flex mensual', 'mensual flexible'] },
  { value: 'flexibleannual', keywords: ['anual flexível', 'anual flexivel', 'anual flexible', 'flex anual'] },
];

export const tierKeywords = [
  { value: 'tier1', keywords: ['nivel 1', 'escala 1', 'tier 1'] },
  { value: 'tier2', keywords: ['nivel 2', 'escala 2', 'tier 2'] },
  { value: 'tier3', keywords: ['nivel 3', 'escala 3', 'tier 3'] },
  { value: 'tier4', keywords: ['nivel 4', 'escala 4', 'tier 4'] },
];

export const tensionLevelAliases = [
  { value: 'bt', keywords: ['bt'] },
  { value: 'btn', keywords: ['btn'] },
  { value: 'mt', keywords: ['mt'] },
  { value: 'at', keywords: ['at'] }
];

export const pressureLevelAliases = [
  { value: 'bp', keywords: ['bp'] },
  { value: 'ap', keywords: ['ap'] }
];